import { useEffect, useState } from "react";
import { crewOpsApi } from "@/api";
import { FileText, User, Settings, Zap, Shield } from "lucide-react";

const actionColors = {
  ROSTER_CREATED: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  COMPLIANCE_CHECK: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  MANUAL_OVERRIDE: "text-orange-400 bg-orange-400/10 border-orange-400/20",
  AUTO_ASSIGN: "text-green-400 bg-green-400/10 border-green-400/20",
  LEAVE_APPROVED: "text-purple-400 bg-purple-400/10 border-purple-400/20",
  FATIGUE_ALERT: "text-red-400 bg-red-400/10 border-red-400/20",
  RECOVERY_ACTION: "text-primary bg-primary/10 border-primary/20",
};

const actorIcons = {
  user: User,
  system: Settings,
  optimizer: Zap,
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState("");

  useEffect(() => {
    crewOpsApi.getAuditLog({ entity_type: filterEntity || undefined }).then(r => setLogs(r.data)).finally(() => setLoading(false));
  }, [filterEntity]);

  const entityTypes = [...new Set(logs.map(l => l.entity_type))];

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="audit-log">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Crew Rostering · Module E</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Audit Log</h1>
        <p className="text-xs text-muted-foreground mt-1">Immutable trail of all roster decisions, overrides, and system actions</p>
      </div>

      <div className="flex items-center gap-3">
        <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
          <option value="">All Entities</option>
          {entityTypes.map(e => <option key={e} value={e}>{e?.replace(/_/g, " ")}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{logs.length} entries</span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Loading audit log...</div>
      ) : (
        <div className="space-y-2">
          {logs.map(log => {
            const ActionColor = actionColors[log.action] || "text-muted-foreground border-border bg-muted/10";
            const ActorIcon = actorIcons[log.actor_type] || User;
            return (
              <div key={log.id} className="bg-card border border-border rounded-sm p-4 fade-in-up" data-testid={`audit-entry-${log.action}`}>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-sm bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                    <ActorIcon size={13} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold border ${ActionColor}`}>{log.action?.replace(/_/g, " ")}</span>
                      <span className="text-xs text-muted-foreground capitalize">{log.entity_type?.replace(/_/g, " ")}</span>
                      {log.actor_id && <span className="text-xs text-foreground font-medium">by {log.actor_id}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{log.reason}</p>
                    {(log.before_json || log.after_json) && (
                      <div className="flex gap-3 mt-2">
                        {log.before_json && (
                          <div className="text-xs bg-red-500/5 border border-red-500/10 rounded-sm px-2 py-1">
                            <span className="text-red-400 font-bold">Before: </span>
                            <span className="text-muted-foreground">{JSON.stringify(log.before_json)}</span>
                          </div>
                        )}
                        {log.after_json && (
                          <div className="text-xs bg-green-500/5 border border-green-500/10 rounded-sm px-2 py-1">
                            <span className="text-green-400 font-bold">After: </span>
                            <span className="text-muted-foreground">{JSON.stringify(log.after_json)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 font-mono">
                    {new Date(log.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
